import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { Check, Copy, Store, Users } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Button, Empty, ErrorBlock, Input, Spinner } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { orgFetch } from "../../api/org-client.ts";
import { useIdentities, useMe } from "../../api/queries.ts";
import type { InviteInfo } from "../invite-share.tsx";
import { shareOrigin } from "../../lib/site-config.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 邀请客户
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **网页上二维码是拿来"给别人扫"的，所以要大。**
 *    手机上的用法是长按保存、发到群里；电脑上的用法完全不同 ——
 *    **把屏幕转过去让客户当场扫**（在店里、在客户家里谈的时候）。
 *    所以这里给它一整块 260px 的方，而不是手机上那个 192 的缩略图。
 *
 * 🔴 **联系方式排在最前面。** 拉来的客户下单之后要联系他付款，
 *    没有联系方式的话前面所有的推广动作都白费。
 */
export function DesktopInviteShare() {
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));
  const [copied, setCopied] = useState<"link" | "code" | null>(null);
  const [qr, setQr] = useState<string>("");

  /**
   * 谁能拉客户。
   * 🔴 **平台也算** —— 它是链条最上面一级。
   * ⚠️ 供应商不算 —— 他供货不零售，拉来的客户挂在他名下没有意义。
   */
  const orgs = (identities?.orgs ?? []).filter(
    (o) =>
      (o.type === "platform" || o.type === "dealer" || o.type === "distributor") &&
      o.status === "active",
  );
  const [pickedId, setPicked] = useState<number | null>(null);
  const org = orgs.find((o) => o.id === pickedId) ?? orgs[0];

  const invite = useQuery({
    queryKey: ["invite", org?.id],
    queryFn: () => orgFetch<InviteInfo>("/invite", org!.id),
    enabled: Boolean(org),
  });

  const shareUrl = invite.data ? `${shareOrigin()}/i/${invite.data.inviteCode}` : "";

  /**
   * 画二维码。
   * ⚠️ 容错级别用 M（15%）—— 太低的话打印出来沾点灰就扫不出，
   *    太高会让码变密、手机镜头对不准。
   */
  useEffect(() => {
    if (!shareUrl) return;
    void QRCode.toDataURL(shareUrl, {
      width: 720,
      margin: 1,
      errorCorrectionLevel: "M",
      color: { dark: "#3d2b1f", light: "#ffffff" },
    }).then(setQr);
  }, [shareUrl]);

  async function copy(text: string, what: "link" | "code") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /** ⚠️ 非 https 下 clipboard 不可用 —— 静默失败，内容就在眼前他能自己选。 */
    }
  }

  if (mePending)
    return (
      <AccountShell title="邀请客户">
        <Spinner compact />
      </AccountShell>
    );

  if (!me || orgs.length === 0)
    return (
      <AccountShell title="邀请客户">
        <Empty title="你还不是商家" hint="邀请客户是商家功能。想开店请联系上级商家或平台。" compact />
      </AccountShell>
    );

  return (
    <AccountShell title="邀请客户">
      {orgs.length > 1 && (
        <div className="mb-4 flex gap-2">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={cn(
                "rounded-lg px-3.5 py-1.5 text-[14px] transition-colors",
                o.id === org?.id
                  ? "bg-brand-600 text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      {invite.isPending ? (
        <Spinner compact />
      ) : invite.error ? (
        <ErrorBlock error={invite.error} />
      ) : (
        <div className="grid grid-cols-[300px_minmax(0,1fr)] items-start gap-8">
          {/* ══════════ 左：二维码 ══════════ */}
          <div className="rounded-xl border border-border bg-card p-6 text-center">
            <p className="text-[16px] font-medium">{org?.name}</p>
            {/* ⚠️ 电脑上的用法是"把屏幕转过去让客户扫"，跟手机上完全不同。 */}
            <p className="mt-1 text-[13px] text-muted-foreground">
              把屏幕转过去，让客户扫这个码
            </p>

            <div className="mt-4">
              {qr ? (
                <img
                  src={qr}
                  alt="我的邀请二维码"
                  className="w-full rounded-xl border border-border"
                />
              ) : (
                <div className="aspect-square w-full animate-pulse rounded-xl bg-secondary" />
              )}
            </div>

            <div className="mt-5 flex gap-4">
              <div className="flex-1">
                <p className="text-[22px] font-medium tabular-nums">{invite.data?.visits ?? 0}</p>
                <p className="mt-0.5 text-[12px] text-muted-foreground">被打开</p>
              </div>
              <div className="flex-1">
                <p className="text-[22px] font-medium text-brand-600 tabular-nums">
                  {invite.data?.registered ?? 0}
                </p>
                <p className="mt-0.5 text-[12px] text-muted-foreground">成了我的客户</p>
              </div>
            </div>
          </div>

          {/* ══════════ 右：联系方式 / 链接 / 规则 ══════════ */}
          <div className="space-y-4">
            {org && <ContactCard orgId={org.id} canHaveShop={org.type !== "platform"} />}

            <div className="divide-y divide-border rounded-xl border border-border bg-card">
              <div className="px-5 py-4">
                <p className="mb-2 text-[13px] text-muted-foreground">我的专属链接</p>
                <div className="flex items-center gap-3">
                  <span className="min-w-0 flex-1 truncate font-mono text-[14px]">{shareUrl}</span>
                  <Button size="sm" variant="outline" onClick={() => copy(shareUrl, "link")}>
                    {copied === "link" ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                    {copied === "link" ? "已复制" : "复制"}
                  </Button>
                </div>
              </div>

              <div className="px-5 py-4">
                <p className="mb-2 text-[13px] text-muted-foreground">
                  邀请码（客户注册时手动填）
                </p>
                <div className="flex items-center gap-3">
                  <span className="flex-1 font-mono text-[22px] tracking-widest">
                    {invite.data?.inviteCode}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copy(invite.data!.inviteCode, "code")}
                  >
                    {copied === "code" ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                    {copied === "code" ? "已复制" : "复制"}
                  </Button>
                </div>
              </div>
            </div>

            {/*
              🔴 把规则说清楚 —— 分销商最常问的两件事就是
                 "他从别人那儿也扫了怎么办"和"这单算不算我的"。
            */}
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="mb-2.5 flex items-center gap-2 text-[15px] font-medium">
                <Users className="size-[18px] text-muted-foreground" />
                客户是怎么算成你的
              </p>
              <ul className="space-y-2 text-[13px] leading-relaxed text-muted-foreground">
                <li>
                  · 客户从你的码或链接进来<strong className="text-foreground">下单</strong>
                  ，就成了你的客户
                </li>
                <li>
                  · 之后他<strong className="text-foreground">不管从哪进来</strong>下单，都算你的
                </li>
                <li>
                  · <strong className="text-foreground">先到先得</strong> ——
                  已经是别人客户的人，扫你的码也不会转到你名下
                </li>
                <li>· 他看到的是你店里的价，比平台指导价便宜</li>
              </ul>
            </div>

            {/* ⚠️ 平台没有"店铺页"（那是给经销商/分销商的），这条入口对它藏掉。 */}
            {org?.type !== "platform" && (
              <Link
                to="/shop/$id"
                params={{ id: String(org!.id) }}
                className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-4 transition-colors hover:border-brand-200"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-50">
                  <Store className="size-[18px] text-brand-600" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-medium">看看我的店长什么样</span>
                  <span className="block text-[13px] text-muted-foreground">
                    客户扫码进来看到的就是这个
                  </span>
                </span>
              </Link>
            )}
          </div>
        </div>
      )}
    </AccountShell>
  );
}

/**
 * 收款联系方式。
 *
 * 🔴 **这个商城不走线上支付**：买家下完单要直接联系卖家把钱付了。
 *    没填联系方式的话，他的付款页上只有一句"联系商家"却不知道联系谁 ——
 *    这单基本就黄了。所以没填时这张卡片要**主动报警**，不是安静地空着。
 */
function ContactCard({ orgId, canHaveShop }: { orgId: number; canHaveShop: boolean }) {
  const qc = useQueryClient();
  const shop = useQuery({
    queryKey: ["merchant", "shop", orgId],
    queryFn: () => orgFetch<{ contactName: string; contactPhone: string }>("/shop", orgId),
    /**
     * 🔴 **平台身份不发这个请求。** 服务端 `/merchant/shop` 走 `anyMerchant`
     *    （只放 supplier / dealer / distributor），平台调它必然 403 ——
     *    而平台是能进这一页的（它也要拉客户）。判断口径跟「我的」页的
     *    `hasOwnShop` 一致。
     * ⚠️ 手机版那份原来用的是**裸 fetch、不看 `r.ok`**，403 的错误体被当成数据
     *    写进了这个共享 queryKey，返回「我的」时那边 `.trim()` 直接整页白屏。
     *    这一份用 `orgFetch`（会抛），没中招，但请求本身照样是白发的。
     */
    enabled: canHaveShop,
  });

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  const save = useMutation({
    mutationFn: () =>
      orgFetch("/shop", orgId, {
        method: "PATCH",
        body: JSON.stringify({ contactName: name, contactPhone: phone }),
      }),
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["merchant", "shop", orgId] });
    },
  });

  const filled = Boolean(shop.data?.contactPhone);

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <p className="text-[15px] font-medium">收款联系方式</p>
        {!editing && (
          <button
            className="text-[13px] text-brand-600 transition-colors hover:text-brand-700"
            onClick={() => {
              setName(shop.data?.contactName ?? "");
              setPhone(shop.data?.contactPhone ?? "");
              setEditing(true);
            }}
          >
            {filled ? "修改" : "去填写"}
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-3.5">
          <div className="grid grid-cols-2 gap-3">
            <Input label="称呼" value={name} placeholder="王师傅" onChange={(e) => setName(e.target.value)} />
            <Input
              label="电话"
              value={phone}
              placeholder="13800138000"
              inputMode="tel"
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
              保存
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              取消
            </Button>
          </div>
          {save.error && <ErrorBlock error={save.error} />}
        </div>
      ) : filled ? (
        <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
          {shop.data?.contactName && `${shop.data.contactName} · `}
          {shop.data?.contactPhone}
          <br />
          客户下单后会看到这个，联系你付款。
        </p>
      ) : (
        /*
          🔴 没填是**会直接丢单**的状态，所以用警示色主动提醒 ——
             安静地空着的话，商家永远不会意识到问题出在这里。
        */
        <p className="mt-2 rounded-lg bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-800">
          还没填。客户下单后要联系你付款，没有电话他就只能干等 —— 这单大概率就丢了。
        </p>
      )}
    </div>
  );
}
